import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../context/SocketContext";
import { IoSendOutline } from "react-icons/io5";
import { Loader2, MoreVertical, X, MessageCircle } from "lucide-react";
import { Drawer } from "@material-tailwind/react";
import { IoMdClose, IoIosArrowForward } from "react-icons/io";
import { BeatLoader } from "react-spinners";
import toast from "react-hot-toast";
import SkeletonTwo from "../../components/skeleton/SkeletonTwo";
import { postNeighborhoodCommunityChat } from "../../redux/ApiSlice/Neighborhood/NeighborhoodCommunityChatSlice";
import { getReportReasons, reportCommunityMessage } from "../../redux/ApiSlice/ReportSlice";

const Chat = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { neighborhoodCommunityChat, loading } = useSelector((state) => state?.NeighborhoodCommunityChat);
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const { reportReasons = [], reportReasonsLoading } = useSelector((state) => state.Report);
    const { communityChat } = useSocket();
    const communityChatSocket = communityChat?.socket || null;
    const isConnected = communityChat?.isConnected || false;
    const sendMessage = communityChat?.sendMessage || (() => false);

    // Always-fresh ref so effects can read latest context data without stale closure
    const communityDataRef = useRef({});
    communityDataRef.current = {
        messages: communityChat?.messages || [],
        hasMore: communityChat?.hasMore || false,
        nextCursor: communityChat?.nextCursor || null,
        nextBucket: communityChat?.nextBucket || null,
    };

    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [nextBucket, setNextBucket] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const prevScrollHeightRef = useRef(0);
    const isLoadingMoreRef = useRef(false);
    const initialLoadDoneRef = useRef(false);
    const [message, setMessage] = useState("");

    const formatMessages = (msgs) =>
        (msgs || [])
            .map(msg => ({
                id: msg.message_id || msg.created_at,
                message: msg.message,
                sender_id: msg.sender?.id,
                sender_name: msg.sender?.full_name || '',
                avatar: msg.sender?.profile_pic,
                created_at: msg.created_at,
                is_sent_by_current_user: msg.is_sent_by_current_user,
                sender: msg.sender,
                is_edited: msg.is_edited || false,
            }))
            .filter(msg => msg.message?.trim());

    // Initialise from context cache — messages SocketContext already received on connect
    const [chatList, setChatList] = useState(() => formatMessages(communityDataRef.current.messages));
    const prevChatLengthRef = useRef(0);
    const [activeMessageMenu, setActiveMessageMenu] = useState(null);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);

    // Report message drawer
    const [reportMessageId, setReportMessageId] = useState(null);
    const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
    const [reportStep, setReportStep] = useState("reason");
    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedSubReason, setSelectedSubReason] = useState("");
    const [reportNote, setReportNote] = useState("");
    const [reportLoading, setReportLoading] = useState(false);
    const [messageReportSuccess, setMessageReportSuccess] = useState(false);
    const [reportError, setReportError] = useState("");
    const isReportSubmitDisabled = reportLoading || !selectedReason;

    // useEffect(() => {
    //     if (neighborhoodCommunityChat) {
    //         setChatList(neighborhoodCommunityChat?.messages);
    //     }
    // }, [neighborhoodCommunityChat]);

    useEffect(() => {
        if (!activeMessageMenu) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setActiveMessageMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMessageMenu]);

    useLayoutEffect(() => {
        if (!chatContainerRef.current) return;

        if (prevScrollHeightRef.current > 0) {
            // Restore scroll position after load-more prepend
            const newScrollHeight = chatContainerRef.current.scrollHeight;
            chatContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
            prevScrollHeightRef.current = 0;
        } else if (prevChatLengthRef.current === 0 && chatList.length > 0) {
            // Initial load — scroll to bottom instantly before paint so scrollTop
            // is never 0, preventing the scroll handler from firing load_more early
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatList]);

    useEffect(() => {
        const container = chatContainerRef.current;
        const isNewRealTimeMessage = chatList.length > prevChatLengthRef.current && prevChatLengthRef.current > 0;

        if (messagesEndRef.current && container && isNewRealTimeMessage) {
            const hasOverflow = container.scrollHeight > container.clientHeight;
            if (hasOverflow) {
                const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
                if (isNearBottom) {
                    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }
        }

        prevChatLengthRef.current = chatList.length;
    }, [chatList]);

    // Seed chatList and cursors from SocketContext cache whenever the socket changes.
    // The server sends previous_messages automatically on connect; SocketContext's ws.onmessage
    // catches it (before Chat.jsx's addEventListener is registered). Reading from the context
    // ref here ensures the UI is never blank while the live socket is active.
    useEffect(() => {
        if (!communityChatSocket) return;
        const { messages, hasMore: ctxHasMore, nextCursor: ctxNextCursor, nextBucket: ctxNextBucket } = communityDataRef.current;
        const cached = formatMessages(messages);
        if (cached.length > 0) {
            setChatList(cached);
            setHasMore(ctxHasMore);
            setNextCursor(ctxNextCursor);
            setNextBucket(ctxNextBucket);
            initialLoadDoneRef.current = true;
        } else {
            initialLoadDoneRef.current = false;
        }
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
    }, [communityChatSocket]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live socket message handler — updates chatList with new events
    useEffect(() => {
        if (!communityChatSocket) return;

        const handleSocketMessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // Server sends previous_messages: (a) automatically on connect, (b) as load_more response
                if (data.type === 'previous_messages') {
                    const formatted = formatMessages(data.messages);
                    setHasMore(data.has_more || false);
                    setNextCursor(data.next_cursor || null);
                    setNextBucket(data.next_bucket || null);
                    setIsLoadingMore(false);

                    if (isLoadingMoreRef.current) {
                        // load_more response — prepend older messages
                        isLoadingMoreRef.current = false;
                        setChatList(prev => [...formatted, ...prev]);
                    } else {
                        // initial load response
                        setChatList(formatted);
                        initialLoadDoneRef.current = true;
                    }
                    return;
                }

                if (data.type === 'community_message_deleted') {
                    setChatList(prev => prev.filter(m => m.id !== data.message_id));
                    return;
                }

                if (data.type === 'community_message_edited') {
                    setChatList(prev => prev.map(m =>
                        m.id === data.message_id
                            ? { ...m, message: data.new_message, is_edited: true }
                            : m
                    ));
                    return;
                }

                // Real-time new message — no type field per API spec
                if (!data.type && data.message?.trim()) {
                    const msg = {
                        id: data.message_id || data.created_at,
                        message: data.message,
                        sender_id: data.sender?.id,
                        sender_name: data.sender?.full_name || '',
                        avatar: data.sender?.profile_pic,
                        created_at: data.created_at,
                        is_sent_by_current_user: data.is_sent_by_current_user,
                        sender: data.sender,
                    };
                    setChatList(prev => {
                        const isDuplicate = prev.some(m =>
                            m.created_at === msg.created_at && m.message === msg.message
                        );
                        return isDuplicate ? prev : [...prev, msg];
                    });
                }
            } catch (e) { }
        };

        communityChatSocket.addEventListener('message', handleSocketMessage);
        // NOTE: No manual history request — server sends previous_messages automatically on connect (per API spec)

        return () => communityChatSocket.removeEventListener('message', handleSocketMessage);
    }, [communityChatSocket]);

    const loadMore = useCallback(() => {
        if (!initialLoadDoneRef.current) return;
        if (!communityChatSocket || communityChatSocket.readyState !== WebSocket.OPEN) return;
        if (isLoadingMoreRef.current || !hasMore || !nextCursor || !nextBucket) return;

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);

        if (chatContainerRef.current) {
            prevScrollHeightRef.current = chatContainerRef.current.scrollHeight;
        }

        try {
            communityChatSocket.send(JSON.stringify({
                type: 'load_more',
                last_created_at: nextCursor,
                bucket: nextBucket,
            }));
        } catch (e) {
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
            prevScrollHeightRef.current = 0;
        }
    }, [communityChatSocket, hasMore, nextCursor, nextBucket]);

    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (container.scrollTop <= 100) {
                loadMore();
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loadMore]);


    const cancelEdit = () => {
        setEditingMessageId(null);
        setMessage("");
    };

    const handleSendMessage = async () => {
        if (!message || !message.trim()) return;

        if (editingMessageId) {
            handleEditSubmit(editingMessageId);
            return;
        }

        const messageToSend = message.trim();

        if (isConnected && sendMessage) {
            const success = sendMessage({
                message: messageToSend,
                sender_id: User_profile?.id,
                sender_name: `${User_profile?.full_name || ''}`.trim(),
                avatar: User_profile?.profile_pic || User_profile?.avatar_url,
            });

            if (success) {
                setMessage("");
            } else {
                await sendViaAPI(messageToSend);
            }
        } else {
            await sendViaAPI(messageToSend);
        }
    };

    const handleDeleteMessage = (messageId) => {
        if (!communityChatSocket || communityChatSocket.readyState !== WebSocket.OPEN) return;
        try {
            communityChatSocket.send(JSON.stringify({ type: 'delete_message', message_id: messageId }));
        } catch (e) { }
        setActiveMessageMenu(null);
    };

    const handleEditSubmit = (messageId) => {
        const trimmed = message.trim();
        if (!trimmed || !communityChatSocket || communityChatSocket.readyState !== WebSocket.OPEN) return;
        try {
            communityChatSocket.send(JSON.stringify({ type: 'edit_message', message_id: messageId, new_message: trimmed }));
        } catch (e) { }
        setEditingMessageId(null);
        setMessage("");
    };

    const handleOpenReportDrawer = (messageId) => {
        setActiveMessageMenu(null);
        setReportMessageId(messageId);
        dispatch(getReportReasons());
        setReportDrawerOpen(true);
        setReportStep("reason");
        setSelectedReason(null);
        setSelectedSubReason("");
        setReportNote("");
        setMessageReportSuccess(false);
        setReportError("");
    };

    const handleCloseReportDrawer = () => {
        setReportDrawerOpen(false);
        setReportMessageId(null);
        setReportStep("reason");
        setSelectedReason(null);
        setSelectedSubReason("");
        setReportNote("");
        setReportError("");
        setMessageReportSuccess(false);
    };

    const handleReasonSelect = (reasonOption) => {
        setSelectedReason(reasonOption);
        setSelectedSubReason("");
        setReportNote("");
        setReportStep("detail");
        setReportError("");
    };

    const handleBackToReasons = () => {
        setReportStep("reason");
        setSelectedSubReason("");
        setReportNote("");
        setReportError("");
    };

    const handleSubmitReportMessage = async () => {
        if (!reportMessageId) {
            toast.error("No message selected to report");
            return;
        }

        if (!selectedReason) {
            toast.error("Please select a reason for reporting");
            return;
        }

        const requestData = {
            reason: selectedReason.code,
            sub_reason: selectedSubReason,
            user_comment: reportNote.trim(),
        };

        const body = Object.fromEntries(
            Object.entries(requestData).filter(([, v]) => v !== null && v !== undefined)
        );

        try {
            setReportLoading(true);
            setReportError("");
            const result = await dispatch(reportCommunityMessage({ messageId: reportMessageId, data: body }));

            if (reportCommunityMessage.fulfilled.match(result)) {
                setReportError("");
                setMessageReportSuccess(true);
                toast.success("Message reported successfully");
            } else if (reportCommunityMessage.rejected.match(result)) {
                const errorMessage = result.payload?.message || result.payload?.detail || "Failed to report message";
                setReportError(errorMessage);
                setMessageReportSuccess(false);
            }
        } catch (error) {
            const fallbackMessage = error?.response?.data?.message || error?.message || "Failed to report message";
            setReportError(fallbackMessage);
            setMessageReportSuccess(false);
        } finally {
            setReportLoading(false);
        }
    };

    const reportDrawerSize = messageReportSuccess ? 220 : 540;

    const sendViaAPI = async (messageContent) => {
        // Only send if we have a message
        if (!messageContent) return;

        const data = { content: messageContent };

        try {
            const response = await dispatch(postNeighborhoodCommunityChat({ data }));
            if (response.error) {
                console.error("Error sending message:", response.error);
            } else {
                setMessage("");
                // Message will be updated via WebSocket, no need to refetch
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    // useEffect(() => {
    //     dispatch(getNeighborhoodCommunityChats())
    // }, [])

    function getDateKey(dateString) {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    }

    function formatDateHeader(dateString) {
        const now = new Date();
        const date = new Date(dateString);

        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth();
        const nowDate = now.getDate();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        const dateDate = date.getDate();

        let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        timeString = timeString.replace(/am|pm/i, match => match.toUpperCase());

        const monthAbbr = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        const weekdayShort = date.toLocaleString('en-US', { weekday: 'short' }).toUpperCase();

        if (nowYear === dateYear && nowMonth === dateMonth && nowDate === dateDate) {
            return `TODAY ${timeString}`;
        }

        const yesterday = new Date(now);
        yesterday.setDate(nowDate - 1);
        if (
            dateYear === yesterday.getFullYear() &&
            dateMonth === yesterday.getMonth() &&
            dateDate === yesterday.getDate()
        ) {
            return `YESTERDAY ${timeString}`;
        }

        const diffMs = now - date;
        const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDay < 7) return `${weekdayShort} ${timeString}`;
        if (nowYear === dateYear) return `${day} ${monthAbbr} ${timeString}`;
        return `${day} ${monthAbbr} ${year} ${timeString}`;
    }

    const processedChatList = (() => {
        if (!Array.isArray(chatList)) return [];

        const messagesByDate = {};
        chatList.forEach(msg => {
            const dateKey = getDateKey(msg.created_at);
            if (!messagesByDate[dateKey]) messagesByDate[dateKey] = [];
            messagesByDate[dateKey].push(msg);
        });

        return chatList.map((msg, index) => {
            const currentDateKey = getDateKey(msg.created_at);
            const prevMsg = index > 0 ? chatList[index - 1] : null;
            const prevDateKey = prevMsg ? getDateKey(prevMsg.created_at) : null;
            const isFirstOfDate = !prevMsg || currentDateKey !== prevDateKey;

            let dateHeader = null;
            if (isFirstOfDate) {
                const messagesInThisDate = messagesByDate[currentDateKey];
                const latestMessage = messagesInThisDate[messagesInThisDate.length - 1];
                dateHeader = formatDateHeader(latestMessage.created_at);
            }

            return { ...msg, showDateHeader: isFirstOfDate, dateHeader };
        });
    })();

    if (loading) return <SkeletonTwo />


    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            <div className="h-full flex flex-col bg-white dark:bg-[#1E1D21] dark:text-white">
                <div ref={chatContainerRef} className="flex-1 min-h-0 px-2 py-1 overflow-y-scroll no-scrollbar">
                    {isLoadingMore && (
                        <div className="flex justify-center py-2">
                            <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-600 animate-spin" />
                        </div>
                    )}
                    {processedChatList.map((item) => (
                        <div key={item.id}>
                            {item.showDateHeader && (
                                <div className="flex justify-center my-4">
                                    <p className="text-[10px] text-gray-600 dark:text-gray-400 font-medium">
                                        {item.dateHeader}
                                    </p>
                                </div>
                            )}
                            {item.is_sent_by_current_user ?
                                <div className="flex flex-col items-end mb-1">
                                    <div className="group flex items-center gap-1 max-w-[78%]">
                                        {/* three-dots trigger — popup anchored to this button */}
                                        <div className="relative shrink-0">
                                            <button
                                                onClick={() => setActiveMessageMenu(activeMessageMenu === item.id ? null : item.id)}
                                                className="flex items-center justify-center opacity-40 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
                                            >
                                                <MoreVertical size={14} />
                                            </button>
                                            {activeMessageMenu === item.id && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute bottom-full right-0 mb-1 min-w-[96px] bg-white dark:bg-[#1E1D21] rounded-xl shadow-xl border border-gray-200 dark:border-gray-600 z-10 overflow-hidden"
                                                >
                                                    <button
                                                        onClick={() => {
                                                            setEditingMessageId(item.id);
                                                            setMessage(item.message);
                                                            setActiveMessageMenu(null);
                                                            setTimeout(() => inputRef.current?.focus(), 50);
                                                        }}
                                                        className="block w-full px-4 py-2.5 text-[13px] font-medium text-left text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    >
                                                        Edit
                                                    </button>
                                                    <div className="h-px bg-gray-200 dark:bg-gray-700 mx-2" />
                                                    <button
                                                        onClick={() => handleDeleteMessage(item.id)}
                                                        className="block w-full px-4 py-2.5 text-[13px] font-medium text-left text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {/* bubble */}
                                        <div className="min-w-0">
                                            <div className={`px-2 py-1.5 rounded-lg w-fit min-w-0 ${editingMessageId === item.id ? 'bg-blue-400 ring-2 ring-blue-300' : 'bg-blue-500'} text-white`}>
                                                <p className="text-[13px] leading-[15px] select-none break-words">{item.message}</p>
                                                {item.is_edited && (
                                                    <span className="text-[9px] text-blue-200 italic"> edited</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="flex flex-col items-start mb-3">
                                    <div className="flex items-center gap-1">
                                        <img
                                            alt={'User'}
                                            src={item.avatar}
                                            onClick={() => navigate(`/account/${item.sender_id}`)}
                                            className="h-6 w-6 shrink-0 rounded-full object-cover border border-gray-400 dark:border-gray-800"
                                        />
                                        <p className="text-[10px] font-semibold text-black dark:text-white">
                                            {item.sender_name}
                                        </p>
                                    </div>
                                    <div className="group inline-flex items-center gap-1 mt-1 max-w-[78%]">
                                        {/* bubble */}
                                        <div className="min-w-0">
                                            <div className="px-2 py-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg w-fit max-w-[100%] relative">
                                                <p className="text-[13px] text-black dark:text-white leading-[15px] select-none">
                                                    {item.message}
                                                </p>
                                            </div>
                                        </div>
                                        {/* three-dots trigger — popup anchored to this button */}
                                        <div className="relative shrink-0">
                                            <button
                                                onClick={() => setActiveMessageMenu(activeMessageMenu === item.id ? null : item.id)}
                                                className="flex items-center justify-center opacity-40 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
                                            >
                                                <MoreVertical size={14} />
                                            </button>
                                            {activeMessageMenu === item.id && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute bottom-full left-0 mb-1 min-w-[96px] bg-white dark:bg-[#1E1D21] rounded-xl shadow-xl border border-gray-200 dark:border-gray-600 z-10 overflow-hidden"
                                                >
                                                    <button
                                                        onClick={() => handleOpenReportDrawer(item.id)}
                                                        className="block w-full px-4 py-2.5 text-[13px] font-medium text-left text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    >
                                                        Report
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    ))}
                    {chatList?.length === 0 && !loading && (
                        <div className="flex justify-center items-center text-gray-400 dark:text-gray-700 text-[12px] mt-14">
                            No messages yet.
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="shrink-0 sticky bottom-0 bg-white dark:bg-[#1E1D21] z-10 px-2.5 md:px-2 py-1.5">
                    {editingMessageId && (
                        <div className="flex items-center justify-between px-2 py-1 mb-1 bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500 rounded-r-md">
                            <span className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">Editing message</span>
                            <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X size={13} />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center gap-2 my-1 w-full rounded-lg border-2 border-[#b3e1fe] dark:border-gray-800/50 bg-white dark:bg-[#333333] pl-3 pr-1.5 py-1.5">
                        <MessageCircle size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        <input
                            ref={inputRef}
                            required
                            type="text"
                            value={message}
                            placeholder={isConnected ? "Send a message" : "Connecting..."}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onKeyDown={(e) => { if (e.key === 'Escape' && editingMessageId) cancelEdit(); }}
                            disabled={!isConnected}
                            className="text-[14px] w-full bg-transparent text-black dark:text-white focus:outline-none border-none"
                        />
                        <button
                            type="button"
                            onClick={handleSendMessage}
                            disabled={!message.trim() || !isConnected}
                            className={`flex items-center justify-center p-2 rounded-full cursor-pointer shrink-0 transition-colors ${message.trim() && isConnected
                                ? 'bg-blue-500 hover:bg-blue-600'
                                : ' cursor-not-allowed'
                                }`}
                        >
                            <IoSendOutline size={16} className="text-gray-400 dark:text-gray-500" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Report message */}
            {reportDrawerOpen && (
                <Drawer
                    size={reportDrawerSize}
                    placement="bottom"
                    open={reportDrawerOpen}
                    onClose={handleCloseReportDrawer}
                    className="absolute w-full rounded-t-2xl bg-white dark:bg-gray-900 max-h-[90vh] shadow-2xl"
                    style={{ zIndex: 3 }}
                >
                    <div className={`relative flex flex-col ${messageReportSuccess ? "h-auto max-h-[70vh]" : "h-full max-h-[80vh]"}`}>
                        <div className="relative flex items-center justify-center px-4 pt-3 pb-2">
                            {reportStep === "detail" && !messageReportSuccess && (
                                <IoIosArrowForward
                                    onClick={handleBackToReasons}
                                    className="absolute left-4 top-4 cursor-pointer rotate-180 text-[20px] text-gray-800 dark:text-gray-100"
                                />
                            )}
                            {reportStep !== "detail" &&
                                <IoMdClose
                                    onClick={handleCloseReportDrawer}
                                    className="absolute right-4 top-3 cursor-pointer text-[20px] text-gray-800 dark:text-gray-100"
                                />
                            }
                        </div>

                        {!messageReportSuccess && reportStep === "reason" && (
                            <div className="flex h-full flex-col overflow-y-auto pb-6">
                                <div className="px-4 pt-1 pb-4 text-center space-y-1">
                                    <h2 className="text-[16px] font-semibold text-black dark:text-white">Report message</h2>
                                    <p className="text-[11px] text-gray-600 dark:text-gray-300">
                                        Reports are confidential. The person who sent it won't know you reported it.
                                    </p>
                                </div>

                                <div className="border-t border-b border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                                    {reportReasonsLoading ? (
                                        <div className="flex justify-center items-center py-8">
                                            <BeatLoader size={8} color={"#6b7280"} />
                                        </div>
                                    ) : (
                                        reportReasons.map((item) => (
                                            <button
                                                key={item.code}
                                                type="button"
                                                onClick={() => handleReasonSelect(item)}
                                                className="flex w-full items-center justify-between px-5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                            >
                                                <span className="text-[12px] font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                                                <IoIosArrowForward className="text-[16px] text-gray-400" />
                                            </button>
                                        ))
                                    )}
                                </div>

                                <p className="mt-4 px-6 text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                                    Learn more about our <span className="text-blue-500 underline cursor-pointer">Community Guidelines</span>.
                                </p>
                            </div>
                        )}

                        {!messageReportSuccess && reportStep === "detail" && selectedReason && (
                            <div className="flex h-full flex-col overflow-y-auto pb-4">
                                <div className="px-4 pt-2 pb-3 text-center space-y-1">
                                    <h3 className="text-[14px] font-semibold text-black dark:text-white">{selectedReason.label}</h3>
                                    <p className="text-[12px] text-gray-600 dark:text-gray-300">
                                        Tell us more so we can review this message.
                                    </p>
                                </div>

                                <div className="flex flex-col gap-3 px-4">
                                    {selectedReason.sub_reasons?.length > 0 ? (
                                        <div className="overflow-hidden rounded-xl border border-gray-500 dark:border-gray-800 divide-y divide-gray-500 dark:divide-gray-800 bg-white dark:bg-gray-900">
                                            {selectedReason.sub_reasons.map((sub) => (
                                                <button
                                                    key={sub.code}
                                                    type="button"
                                                    onClick={() => setSelectedSubReason(sub.code)}
                                                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                                                >
                                                    <span className="text-[12px] text-gray-900 dark:text-gray-100">{sub.label}</span>
                                                    <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${selectedSubReason === sub.code ? "border-gray-900 dark:border-gray-100" : "border-gray-500 dark:border-gray-700"}`}>
                                                        <span className={`h-2 w-2 rounded-full ${selectedSubReason === sub.code ? "bg-gray-900 dark:bg-gray-100" : "bg-transparent"}`} />
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 p-3 text-[12px] text-gray-600 dark:bg-gray-800 dark:text-gray-200">
                                            No additional categories for this reason. Add optional context below.
                                        </div>
                                    )}

                                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3">
                                        <textarea
                                            value={reportNote}
                                            maxLength={250}
                                            placeholder="Share anything that might help us understand the issue (optional)"
                                            onChange={(e) => setReportNote(e.target.value)}
                                            className="h-[90px] w-full resize-none bg-transparent text-[13px] text-gray-900 placeholder-gray-500 outline-none dark:text-gray-100 dark:placeholder-gray-400"
                                        />
                                        <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                                            {reportError && <span className="text-red-600">{reportError}</span>}
                                            <span>{reportNote.length}/250</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-auto flex gap-3 px-4 pt-4 pb-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseReportDrawer}
                                        className="flex-1 rounded-xl border border-gray-300 bg-white py-2 text-[14px] font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isReportSubmitDisabled}
                                        onClick={handleSubmitReportMessage}
                                        className={`flex flex-1 items-center justify-center rounded-xl py-2 text-[14px] font-semibold transition ${isReportSubmitDisabled ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-800 dark:text-gray-500" : "bg-red-600 text-white hover:bg-red-700"}`}
                                    >
                                        {reportLoading ? <BeatLoader size={10} color={"#fff"} loading={reportLoading} /> : "Submit report"}
                                    </button>
                                </div>

                                <p className="px-6 text-center text-[11px] text-gray-500 dark:text-gray-400">
                                    Reports are confidential. The person who sent it won't know you reported it.
                                </p>
                            </div>
                        )}

                        {messageReportSuccess && (
                            <div className="flex flex-col items-center justify-center gap-4 px-6 pb-6 pt-2 text-center">
                                <p className="text-[20px] font-semibold text-gray-900 dark:text-white">
                                    Thanks for letting us know
                                </p>
                                <p className="text-[11px] text-gray-700 dark:text-gray-300">
                                    We'll review this message and take action if it violates our guidelines.
                                    You may not hear back, but your report helps keep the community safe.
                                </p>
                                <button
                                    onClick={handleCloseReportDrawer}
                                    className="w-full bg-gray-800 text-white rounded-xl py-2"
                                >
                                    Done
                                </button>
                                <p className="px-6 text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                                    Learn more about our <span className="text-blue-500 underline cursor-pointer">Community Guidelines</span>.
                                </p>
                            </div>
                        )}
                    </div>
                </Drawer>
            )}
        </>
    )
}

export default Chat